import { useEffect } from "react";
import { Outlet, useParams } from "react-router";

import {
  FeatureFlag,
  useFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import { useGetCoInvestGpPermissions } from "@vega-api/generated/coinvest-gp-permissions/coinvest-gp-permissions";
import { type BaseNavigationItem } from "@vega-core/components/Navigation/Sidebar/Sidebar.types";
import { subNavigationActions } from "@vega-core/components/Navigation/Sidebar/SubNavigation.store";
import { SubNavigationList } from "@vega-core/components/Navigation/Sidebar/SubNavigationList";
import { m } from "@vega-core/paraglide/messages";
import { PageContainerWithNavigation } from "@vega-modules/components/PageContainerWithNavigation/PageContainerWithNavigation";
import { CoInvestmentFullPaths } from "@vega-modules/paths";
import { CoInvestDealNotificationBell } from "@vega-modules/scenes/CoInvestments/components/CoInvestDealNotificationBell/CoInvestDealNotificationBell";

import { CoInvestDealMessagesPopover } from "../CoInvestDealMessagesPopover/CoInvestDealMessagesPopover";

import { CoInvestDealsDropdown } from "./CoInvestDealsDropdown";

const NavigationElement = ({
  items,
  isNewNavEnabled,
  isNotificationsEnabled,
  isMessagingEnabled,
  dealId,
}: {
  items: BaseNavigationItem[];
  isNewNavEnabled: boolean;
  isNotificationsEnabled: boolean;
  isMessagingEnabled: boolean;
  dealId: string;
}) => (
  <div className="flex items-center">
    {isNewNavEnabled ? (
      <div className="ml-4 my-2" data-testid="coinvest-deals-dropdown">
        <CoInvestDealsDropdown />
      </div>
    ) : undefined}
    <SubNavigationList horizontal items={items} />
    {isNewNavEnabled ? (
      <div className="ml-auto flex items-center gap-4 pr-3">
        {isMessagingEnabled ? (
          <CoInvestDealMessagesPopover dealId={dealId} />
        ) : null}
        {isNotificationsEnabled ? (
          <CoInvestDealNotificationBell metadata={{ dealId }} />
        ) : null}
      </div>
    ) : null}
  </div>
);

export const CoInvestmentNavigationWrapper = () => {
  const { id } = useParams<{ id: string }>();
  const { data: permissions } = useGetCoInvestGpPermissions(
    { dealId: id },
    {
      query: {
        select: (response) => response.data,
      },
    },
  );

  const canUploadDocuments = permissions?.canUploadDealDocuments;
  const canViewTracking = permissions?.canViewDealProgress;

  const { featureFlag } = useFeatureFlag();
  const isNewDataRoomEnabled =
    featureFlag[FeatureFlag.COINVEST_DATA_ROOM_VD_22599];
  const isNewNavEnabled =
    featureFlag[FeatureFlag.GP_COINVEST_NEW_UI_NAV_VD_25769];
  const isNotificationsEnabled =
    featureFlag[FeatureFlag.COINVEST_NOTIFICATIONS];
  const isMessagingEnabled =
    featureFlag[FeatureFlag.COINVEST_MESSAGING_VD_28584];

  useEffect(() => {
    // If not in a specific deal, no need to show sub-navigation
    if (!id) return;

    const items: BaseNavigationItem[] = isNewNavEnabled
      ? [
          {
            name: m.coInvestments_nav_configure(),
            href: CoInvestmentFullPaths.CoInvestmentConfigure.replace(
              ":id",
              id,
            ),
          },
          {
            name: m.coInvestments_nav_share(),
            href: CoInvestmentFullPaths.CoInvestmentShare.replace(":id", id),
          },
          {
            name: m.coInvestments_nav_track(),
            href: CoInvestmentFullPaths.CoInvestmentTrack.replace(":id", id),
          },
          {
            name: m.coInvestments_nav_report(),
            href: CoInvestmentFullPaths.CoInvestmentReport.replace(":id", id),
          },
          {
            name: m.coInvestments_nav_audit(),
            href: CoInvestmentFullPaths.CoInvestmentAudit.replace(":id", id),
          },
        ]
      : [
          {
            name: m.coInvestments_nav_dealInformation(),
            href: CoInvestmentFullPaths.CoInvestmentDetails.replace(":id", id),
          },
          ...(canUploadDocuments
            ? [
                {
                  name: isNewDataRoomEnabled
                    ? m.coInvestments_nav_dataRoom()
                    : m.coInvestments_nav_dataRoomAndNDA(),
                  href: CoInvestmentFullPaths.CoInvestmentDataRoom.replace(
                    ":id",
                    id,
                  ),
                },
              ]
            : []),
          ...(isNewDataRoomEnabled && canUploadDocuments
            ? [
                {
                  name: m.coInvestments_nav_ndaManagement(),
                  href: CoInvestmentFullPaths.CoInvestmentNdaManagement.replace(
                    ":id",
                    id,
                  ),
                },
              ]
            : []),
          {
            name: m.coInvestments_nav_prospects(),
            href: CoInvestmentFullPaths.CoInvestmentProspects.replace(
              ":id",
              id,
            ),
          },
          {
            name: m.coInvestments_nav_outreach(),
            href: CoInvestmentFullPaths.CoInvestmentOutreach.replace(":id", id),
          },
          ...(canViewTracking
            ? [
                {
                  name: m.coInvestments_nav_tracking(),
                  href: CoInvestmentFullPaths.CoInvestmentTracking.replace(
                    ":id",
                    id,
                  ),
                },
              ]
            : []),
          {
            name: m.coInvestments_nav_audit(),
            href: CoInvestmentFullPaths.CoInvestmentAudit.replace(":id", id),
          },
        ];

    subNavigationActions.setSubNav({
      node: (
        <NavigationElement
          dealId={id}
          isMessagingEnabled={isMessagingEnabled}
          isNewNavEnabled={isNewNavEnabled}
          isNotificationsEnabled={isNotificationsEnabled}
          items={items}
        />
      ),
      items,
    });

    return () => {
      subNavigationActions.clean();
    };
  }, [
    id,
    canUploadDocuments,
    canViewTracking,
    isNewDataRoomEnabled,
    isNewNavEnabled,
    isNotificationsEnabled,
    isMessagingEnabled,
  ]);

  return (
    <PageContainerWithNavigation hideFooter horizontalSubnav>
      <Outlet />
    </PageContainerWithNavigation>
  );
};
